// Predefined color palette for team members
// Each color is defined as HSL values for consistency with the design system
export const USER_COLORS = [
  { name: 'coral', hsl: '12 90% 58%', label: 'Koralle' },
  { name: 'amber', hsl: '38 95% 50%', label: 'Bernstein' },
  { name: 'lime', hsl: '78 75% 44%', label: 'Lime' },
  { name: 'teal', hsl: '174 72% 40%', label: 'Petrol' },
  { name: 'sky', hsl: '198 70% 55%', label: 'Himmelblau' },
  { name: 'slate-blue', hsl: '230 65% 58%', label: 'Schieferblau' },
  { name: 'rose', hsl: '340 82% 55%', label: 'Rose' },
  { name: 'crimson', hsl: '355 78% 50%', label: 'Karmin' },
  { name: 'mauve', hsl: '300 45% 55%', label: 'Mauve' },
  { name: 'bronze', hsl: '28 60% 45%', label: 'Bronze' },
];

/**
 * Generates a consistent color for a user based on their ID (fallback if no color stored)
 */
export function getUserColorFallback(userId: string): { name: string; hsl: string; label: string } {
  if (!userId) {
    return USER_COLORS[0];
  }
  
  // Simple hash function to generate a number from a string
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const index = Math.abs(hash) % USER_COLORS.length;
  return USER_COLORS[index];
}

/**
 * Gets the user's color - uses stored color if available, otherwise falls back to hash
 */
export function getUserColor(userId: string, storedColor?: string | null): { name: string; hsl: string; label: string } {
  if (storedColor) {
    const found = USER_COLORS.find(c => c.hsl === storedColor);
    if (found) return found;
  }
  return getUserColorFallback(userId);
}

/**
 * Returns a CSS background style with the user's color at specified opacity
 */
export function getUserColorStyle(userId: string, opacity: number = 0.15, storedColor?: string | null): React.CSSProperties {
  const color = getUserColor(userId, storedColor);
  // Use solid background for higher opacities to ensure white text is readable
  if (opacity >= 0.3) {
    return {
      backgroundColor: `hsl(${color.hsl})`,
      color: 'white',
    };
  }
  return {
    backgroundColor: `hsl(${color.hsl} / ${opacity})`,
    color: `hsl(${color.hsl})`,
  };
}

/**
 * Returns a solid color style for the user
 */
export function getUserSolidColorStyle(userId: string, storedColor?: string | null): React.CSSProperties {
  const color = getUserColor(userId, storedColor);
  return {
    backgroundColor: `hsl(${color.hsl})`,
    color: 'white',
  };
}

/**
 * Returns Tailwind-compatible class names for the user's color
 * Note: Since colors are dynamic, we use inline styles instead
 */
export function getUserColorClasses(userId: string): string {
  return '';
}
